import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useRegisterChatInput } from '../../hooks/useChatInputRegistry';
import { Search, Send, Zap, CheckCircle, Clock, Eye, Loader, Paperclip, Mic, ArrowLeft, Radio, ChevronDown, ChevronUp, Target, Filter, Plus, AlertTriangle, FileText, Image, Table, Brain, X } from 'lucide-react';
import { AGENT_THUMBS } from './V4BriefingShared';
import { CAMPAIGNS_DATA, CAMPAIGN_FILTERS, CAMPAIGN_CHAT_RESPONSES } from '../../data/campaignsData';
import V4AdHocChat from './V4AdHocChat';
import PRIYA_CAMPAIGN_CREATE_SCENARIOS from '../../data/priyaCampaignCreateData';
import PRIYA_CAMPAIGN_DEEP_DIVES from '../../data/priyaCampaignDeepDiveData';

const AGENT_NAMES = { stark: 'Stark', luc: 'Luc', marsi: 'Marsi', sally: 'Sally', zira: 'Zira', tali: 'Tali', finn: 'Finn', wright: 'Wright', buzz: 'Buzz', dora: 'Dora' };

const STATUS_STYLES = {
  live: { label: 'Live', dark: 'text-emerald-400 bg-emerald-400/10', light: 'text-emerald-600 bg-emerald-600/10', icon: Radio },
  ready: { label: 'Ready', dark: 'text-indigo-400 bg-indigo-400/10', light: 'text-indigo-600 bg-indigo-600/10', icon: CheckCircle },
  draft: { label: 'Draft', dark: 'text-white/40 bg-white/[0.06]', light: 'text-slate-500 bg-slate-200/60', icon: FileText },
  paused: { label: 'Paused', dark: 'text-amber-400 bg-amber-400/10', light: 'text-amber-600 bg-amber-600/10', icon: Clock },
};

const ACTION_STATUS = {
  'complete': { icon: CheckCircle, dark: 'text-emerald-400', light: 'text-emerald-600' },
  'in-progress': { icon: Loader, dark: 'text-blue-400', light: 'text-blue-600' },
  'pending-approval': { icon: Clock, dark: 'text-amber-400', light: 'text-amber-600' },
  'recommended': { icon: Zap, dark: 'text-indigo-400', light: 'text-indigo-600' },
  'exploring': { icon: Eye, dark: 'text-white/40', light: 'text-slate-500' },
};

const CHANNEL_LABELS = { linkedin: 'LinkedIn', meta: 'Meta', blog: 'Blog', email: 'Email', multi: 'Multi-Channel' };
const ARTIFACT_ICONS = { pdf: FileText, image: Image, spreadsheet: Table };

/* ── Campaign Card ── */
const CampaignCard = ({ campaign, isDark, onDeepDive, onDetails }) => {
  const hasAlert = campaign.alert;
  const isNew = campaign.isNew;
  const cardBg = isNew
    ? (isDark ? 'bg-indigo-950/20' : 'bg-indigo-50/60')
    : hasAlert
      ? (isDark ? 'bg-red-950/20' : 'bg-red-50/60')
      : (isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]');
  const border = isNew
    ? (isDark ? 'border-indigo-500/25' : 'border-indigo-300/60')
    : hasAlert
      ? (isDark ? 'border-red-500/15' : 'border-red-200/60')
      : (isDark ? 'border-white/[0.06]' : 'border-black/[0.06]');
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const hoverBg = isDark ? 'hover:bg-white/[0.03]' : 'hover:bg-black/[0.02]';
  const status = STATUS_STYLES[campaign.status];
  const StatusIcon = status.icon;
  const thumb = AGENT_THUMBS[campaign.agent];
  const pct = campaign.budget.total > 0 ? Math.round((campaign.budget.spent / campaign.budget.total) * 100) : 0;

  return (
    <div className={`${cardBg} border ${border} rounded-xl px-4 py-3 flex flex-col h-full`} data-testid={`campaign-${campaign.id}`}>
      {/* Header */}
      <div className="flex items-center gap-2.5">
        {thumb && <img src={thumb} alt="" className="w-6 h-6 rounded-full object-cover flex-shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className={`text-sm font-semibold truncate ${tp}`}>{campaign.name}</p>
            {hasAlert && <AlertTriangle className={`w-3.5 h-3.5 flex-shrink-0 ${isDark ? 'text-red-400' : 'text-red-500'}`} />}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className={`text-[10px] ${ts}`}>{CHANNEL_LABELS[campaign.channel]}</span>
            <span className={`text-[10px] ${ts}`}>·</span>
            <span className={`text-[10px] ${ts}`}>{AGENT_NAMES[campaign.agent]}</span>
            <span className={`text-[10px] ${ts}`}>·</span>
            <span className={`text-[10px] ${ts}`}>{campaign.startDate}</span>
          </div>
        </div>
        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap flex items-center gap-1 ${isDark ? status.dark : status.light}`}>
          <StatusIcon className="w-3 h-3" />
          {status.label}
        </span>
      </div>

      {/* Budget */}
      {campaign.budget.total > 0 && (
        <div className="mt-2.5">
          <div className="flex items-center justify-between mb-1">
            <span className={`text-[10px] ${ts}`}>Budget</span>
            <span className={`text-[10px] font-medium ${tp}`}>${(campaign.budget.spent / 1000).toFixed(1)}K / ${(campaign.budget.total / 1000).toFixed(1)}K</span>
          </div>
          <div className={`w-full h-1.5 rounded-full ${isDark ? 'bg-white/[0.06]' : 'bg-black/[0.06]'}`}>
            <div className={`h-full rounded-full ${hasAlert ? 'bg-gradient-to-r from-red-500 to-amber-500' : 'bg-gradient-to-r from-indigo-500 to-purple-500'}`} style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}

      {/* Metrics */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2.5">
        {campaign.metrics.map((m, i) => (
          <div key={i} className="flex items-center gap-1">
            <span className={`text-[10px] ${ts}`}>{m.label}:</span>
            <span className={`text-[10px] font-medium ${m.alert ? (isDark ? 'text-red-400' : 'text-red-600') : tp}`}>{m.value}</span>
            {m.alert && m.target && <span className={`text-[9px] ${ts}`}>(target: {m.target})</span>}
          </div>
        ))}
      </div>

      {/* Description — fixed 2-line height for visual alignment across the row */}
      <p className={`text-[11px] mt-2 leading-relaxed line-clamp-2 min-h-[2.6em] ${ts}`}>{campaign.description}</p>

      {/* Spacer pushes action row to the bottom so CTAs align across all cards in the row */}
      <div className="flex-1" />

      {/* Action buttons (pinned to bottom) */}
      <div className="flex items-center justify-between gap-2 mt-2.5">
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => onDeepDive?.(campaign.id)}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-medium transition-colors ${isDark ? 'bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30' : 'bg-indigo-500/15 text-indigo-600 hover:bg-indigo-500/25'}`}
            data-testid={`campaign-deep-dive-${campaign.id}`}
          >
            <Brain className="w-3 h-3" /> Deep Dive
          </button>
          <button
            onClick={() => onDetails?.(campaign.id)}
            className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium border ${border} ${ts} ${hoverBg}`}
            data-testid={`campaign-details-${campaign.id}`}
          >
            <Eye className="w-3 h-3" /> Details
          </button>
        </div>
        <div className="flex items-center gap-1.5">
          {campaign.status === 'ready' && (
            <button className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-medium transition-colors ${isDark ? 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20' : 'bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/15'}`} data-testid={`campaign-launch-${campaign.id}`}>
              <Radio className="w-3 h-3" /> Launch
            </button>
          )}
          {campaign.status === 'live' && (
            <button className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-medium transition-colors ${isDark ? 'bg-amber-500/10 text-amber-400 hover:bg-amber-500/20' : 'bg-amber-500/10 text-amber-600 hover:bg-amber-500/15'}`} data-testid={`campaign-pause-${campaign.id}`}>
              <Clock className="w-3 h-3" /> Pause
            </button>
          )}
          {campaign.status === 'paused' && (
            <button className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-medium transition-colors ${isDark ? 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20' : 'bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/15'}`} data-testid={`campaign-resume-${campaign.id}`}>
              <Radio className="w-3 h-3" /> Resume
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/* ── Details Panel (slide-in right drawer) ── */
const DetailsPanel = ({ campaign, isDark, onClose, onApproveAction }) => {
  const [showThinking, setShowThinking] = useState(false);
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.08]' : 'border-black/[0.08]';
  const panelBg = isDark ? 'bg-[#0a1628]' : 'bg-[#eee8df]';
  const hoverBg = isDark ? 'hover:bg-white/[0.05]' : 'hover:bg-black/[0.03]';

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  if (!campaign) return null;
  const status = STATUS_STYLES[campaign.status];
  const StatusIcon = status.icon;
  const thumb = AGENT_THUMBS[campaign.agent];

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 ${isDark ? 'bg-black/40' : 'bg-black/20'} backdrop-blur-sm`}
        onClick={onClose}
        data-testid="campaign-details-backdrop"
      />
      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 z-50 h-full w-[480px] ${panelBg} border-l ${border} overflow-y-auto shadow-2xl`}
        style={{ animation: 'slideInRight 0.25s ease-out' }}
        data-testid={`campaign-details-panel-${campaign.id}`}
      >
        {/* Header */}
        <div className={`sticky top-0 ${panelBg} border-b ${border} px-5 py-4 flex items-start gap-3 z-10`}>
          {thumb && <img src={thumb} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0 mt-0.5" />}
          <div className="flex-1 min-w-0">
            <p className={`text-[15px] font-semibold ${tp}`}>{campaign.name}</p>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className={`text-[11px] ${ts}`}>{CHANNEL_LABELS[campaign.channel]}</span>
              <span className={`text-[10px] ${ts}`}>·</span>
              <span className={`text-[11px] ${ts}`}>{AGENT_NAMES[campaign.agent]}</span>
              <span className={`text-[10px] ${ts}`}>·</span>
              <span className={`text-[11px] ${ts}`}>{campaign.startDate}</span>
              <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap flex items-center gap-1 ${isDark ? status.dark : status.light}`}>
                <StatusIcon className="w-3 h-3" />
                {status.label}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`flex-shrink-0 p-1.5 rounded-md ${ts} ${hoverBg}`}
            data-testid="campaign-details-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-5">
          {/* Description */}
          <div>
            <p className={`text-[10px] font-semibold uppercase tracking-wider mb-1.5 ${ts}`}>Summary</p>
            <p className={`text-[12px] leading-relaxed ${tp}`}>{campaign.description}</p>
          </div>

          {/* Metrics */}
          {campaign.metrics?.length > 0 && (
            <div>
              <p className={`text-[10px] font-semibold uppercase tracking-wider mb-1.5 ${ts}`}>Metrics</p>
              <div className={`grid grid-cols-2 gap-2 rounded-lg border ${border} overflow-hidden`}>
                {campaign.metrics.map((m, i) => (
                  <div key={i} className={`px-3 py-2 ${i % 2 === 0 ? (isDark ? 'bg-white/[0.02]' : 'bg-black/[0.02]') : ''}`}>
                    <p className={`text-[10px] ${ts}`}>{m.label}</p>
                    <p className={`text-[12px] font-semibold mt-0.5 ${m.alert ? (isDark ? 'text-red-400' : 'text-red-600') : tp}`}>
                      {m.value}
                      {m.alert && m.target && <span className={`text-[9px] font-normal ml-1 ${ts}`}>(target: {m.target})</span>}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Thinking trail */}
          {campaign.thinking && (
            <div>
              <button
                onClick={() => setShowThinking(!showThinking)}
                className={`flex items-center gap-1.5 text-[10px] font-medium ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`}
                data-testid={`campaign-thinking-toggle-${campaign.id}`}
              >
                <Brain className="w-3 h-3" /> {showThinking ? 'Hide Thinking' : 'Show Thinking Trail'}
              </button>
              {showThinking && (
                <div className={`mt-2 px-3 py-2.5 rounded-lg text-[11px] leading-relaxed ${isDark ? 'bg-indigo-500/5 border border-indigo-500/10 text-indigo-300' : 'bg-indigo-500/5 border border-indigo-500/10 text-indigo-700'}`}>
                  {campaign.thinking}
                </div>
              )}
            </div>
          )}

          {/* Recommendations */}
          {campaign.recommendations?.length > 0 && (
            <div>
              <p className={`text-[10px] font-semibold uppercase tracking-wider mb-1.5 ${ts}`}>Agent Recommendations</p>
              <div className="flex flex-col gap-1.5">
                {campaign.recommendations.map((r, i) => {
                  const st = ACTION_STATUS[r.status] || ACTION_STATUS['exploring'];
                  const Icon = st.icon;
                  const rThumb = AGENT_THUMBS[r.agent];
                  return (
                    <div key={i} className={`flex items-center gap-2 px-2.5 py-2 rounded-md ${isDark ? 'bg-white/[0.02]' : 'bg-black/[0.02]'}`}>
                      <Icon className={`w-3 h-3 flex-shrink-0 ${isDark ? st.dark : st.light}`} />
                      <span className={`text-[11px] flex-1 ${tp}`}>{r.label}</span>
                      {rThumb && <img src={rThumb} alt="" className="w-4 h-4 rounded-full object-cover" />}
                      {r.status === 'pending-approval' && (
                        <button
                          onClick={() => onApproveAction?.(campaign.id, i)}
                          className={`text-[10px] font-medium px-2 py-0.5 rounded-md transition-colors ${isDark ? 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20' : 'bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/15'}`}
                          data-testid={`campaign-approve-rec-${campaign.id}-${i}`}
                        >
                          Approve
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Artifacts */}
          {campaign.artifacts?.length > 0 && (
            <div>
              <p className={`text-[10px] font-semibold uppercase tracking-wider mb-1.5 ${ts}`}>Attachments</p>
              <div className="flex flex-wrap gap-1.5">
                {campaign.artifacts.map((a, i) => {
                  const AIcon = ARTIFACT_ICONS[a.type] || FileText;
                  return (
                    <div key={i} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border ${border} text-[11px] ${ts}`}>
                      <AIcon className="w-3 h-3" />
                      {a.name}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
      <style>{`@keyframes slideInRight { from { transform: translateX(20px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>
    </>
  );
};

/* ── Summary Stats ── */
const SummaryStats = ({ campaigns, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const cardBg = isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]';

  const live = campaigns.filter(c => c.status === 'live').length;
  const ready = campaigns.filter(c => c.status === 'ready').length;
  const draft = campaigns.filter(c => c.status === 'draft').length;
  const paused = campaigns.filter(c => c.status === 'paused').length;
  const totalBudget = campaigns.reduce((s, c) => s + c.budget.total, 0);
  const totalSpent = campaigns.reduce((s, c) => s + c.budget.spent, 0);
  const alertCount = campaigns.filter(c => c.alert).length;

  const stats = [
    { label: 'Live', value: live, color: isDark ? 'text-emerald-400' : 'text-emerald-600' },
    { label: 'Ready', value: ready, color: isDark ? 'text-indigo-400' : 'text-indigo-600' },
    { label: 'Draft', value: draft, color: ts },
    { label: 'Paused', value: paused, color: isDark ? 'text-amber-400' : 'text-amber-600' },
    { label: 'Budget Used', value: `$${(totalSpent / 1000).toFixed(1)}K / $${(totalBudget / 1000).toFixed(1)}K` },
    { label: 'Needs Attention', value: alertCount, color: alertCount > 0 ? (isDark ? 'text-red-400' : 'text-red-500') : ts },
  ];

  return (
    <div className="flex items-center gap-4" data-testid="campaign-stats">
      {stats.map((s, i) => (
        <div key={i} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border ${border} ${cardBg}`}>
          <span className={`text-[10px] ${ts}`}>{s.label}</span>
          <span className={`text-xs font-bold ${s.color || tp}`}>{s.value}</span>
        </div>
      ))}
    </div>
  );
};

/* ── Chat Message ── */
const ChatMessage = ({ msg, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';

  if (msg.role === 'user') {
    return (
      <div className="flex justify-end" data-testid="campaign-chat-msg-user">
        <div className={`max-w-[75%] px-3.5 py-2.5 rounded-2xl rounded-br-md text-sm leading-relaxed ${isDark ? 'bg-indigo-500/15 text-white' : 'bg-indigo-500/10 text-slate-800'}`}>
          {msg.text}
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-3" data-testid="campaign-chat-msg-agent">
      <img src={AGENT_THUMBS.marsi} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0 mt-1" />
      <div className={`text-sm leading-relaxed whitespace-pre-line ${tp}`}>{msg.text}</div>
    </div>
  );
};

/* ════════════════════════════════════════ */
/* ── Main Component ── */
/* ════════════════════════════════════════ */

const V4MktCampaigns = ({ isDark, onWorkflowToggle, workflowPanelOpen, priyaState }) => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [channelFilter, setChannelFilter] = useState('all');
  const [agentFilter, setAgentFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [approvedRecs, setApprovedRecs] = useState(new Set());
  const [message, setMessage] = useState('');
  useRegisterChatInput(setMessage);
  const [chatMode, setChatMode] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [deepDiveId, setDeepDiveId] = useState(null);
  const [newCampaignFlow, setNewCampaignFlow] = useState(false);
  const [detailsId, setDetailsId] = useState(null);
  const messagesEndRef = useRef(null);

  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const solidBg = isDark ? 'bg-[#0a1628]' : 'bg-[#eee8df]';
  const inputBorder = isDark ? 'border-white/[0.08]' : 'border-black/[0.08]';
  const hoverBg = isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]';
  const pillBg = isDark ? 'bg-white/[0.04]' : 'bg-black/[0.03]';
  const pillActive = isDark ? 'bg-indigo-500/15 text-indigo-400 border-indigo-500/20' : 'bg-indigo-500/10 text-indigo-600 border-indigo-500/20';

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const allCampaigns = useMemo(() => {
    const created = priyaState?.createdCampaigns || [];
    return [...created, ...CAMPAIGNS_DATA];
  }, [priyaState?.createdCampaigns]);

  const filtered = useMemo(() => {
    return allCampaigns.filter(c => {
      if (statusFilter !== 'all' && c.status !== statusFilter) return false;
      if (channelFilter !== 'all' && c.channel !== channelFilter) return false;
      if (agentFilter !== 'all' && c.agent !== agentFilter) return false;
      if (search) {
        const s = search.toLowerCase();
        return c.name.toLowerCase().includes(s) || c.description.toLowerCase().includes(s) || c.channel.toLowerCase().includes(s);
      }
      return true;
    });
  }, [allCampaigns, search, statusFilter, channelFilter, agentFilter]);

  const handleApproveAction = (campaignId, recIndex) => {
    setApprovedRecs(prev => new Set([...prev, `${campaignId}-${recIndex}`]));
  };

  const sendMessage = (text, responseData) => {
    const userMsg = { role: 'user', text };
    const agentMsg = { role: 'agent', text: responseData?.text || CAMPAIGN_CHAT_RESPONSES.general.text };
    setChatMessages(prev => [...prev, userMsg, agentMsg]);
    setChatMode(true);
  };

  const handleSend = () => {
    if (message.trim()) {
      sendMessage(message.trim(), null);
      setMessage('');
    }
  };

  const handleCreateCampaign = () => {
    setNewCampaignFlow(true);
  };

  const handleDeepDive = (id) => {
    setDeepDiveId(id);
  };

  const handleBackFromDeepDive = () => {
    setDeepDiveId(null);
  };

  const handleBackFromNewCampaign = () => {
    const scenario = PRIYA_CAMPAIGN_CREATE_SCENARIOS['new-campaign'];
    if (scenario?.createdCampaign && priyaState?.createPriyaCampaign) {
      priyaState.createPriyaCampaign(scenario.createdCampaign);
    }
    setNewCampaignFlow(false);
  };

  const handleInput = (e) => {
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px';
    setMessage(e.target.value);
  };

  /* ── Chat input ── */
  const chatInput = (
    <div className="flex justify-center pb-4 px-4 flex-shrink-0">
      <div className="w-[80%]">
        <div className={`${solidBg} border ${inputBorder} rounded-2xl flex flex-col`}>
          <div className="px-4 pt-3 pb-2">
            <textarea
              value={message}
              onChange={handleInput}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Message Marsi about campaigns..."
              rows={1}
              className={`w-full bg-transparent resize-none outline-none text-sm leading-5 ${tp} placeholder:${ts}`}
              style={{ height: 'auto', maxHeight: '200px', overflow: message.split('\n').length > 8 ? 'auto' : 'hidden' }}
              data-testid="campaign-chat-input"
            />
          </div>
          <div className="flex items-center justify-between px-3 pb-3">
            <div className="flex items-center gap-1">
              <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg ${isDark ? 'bg-white/[0.04]' : 'bg-black/[0.03]'}`}>
                <img src={AGENT_THUMBS.marsi} alt="" className="w-5 h-5 rounded-full object-cover" />
                <span className={`text-xs font-medium ${tp}`}>Marsi</span>
              </div>
              <button onClick={onWorkflowToggle} className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg transition-colors ${workflowPanelOpen ? (isDark ? 'bg-white/[0.08]' : 'bg-black/[0.06]') : hoverBg}`} data-testid="campaign-workflow-btn">
                <Zap className={`w-3.5 h-3.5 ${tp}`} />
                <span className={`text-xs font-medium ${tp}`}>Workflows</span>
              </button>
              <button className={`p-1.5 rounded-lg transition-colors ${hoverBg}`}>
                <Paperclip className={`w-3.5 h-3.5 ${ts}`} />
              </button>
            </div>
            <div className="flex items-center gap-1.5">
              <button className={`w-8 h-8 rounded-xl flex items-center justify-center transition-colors ${hoverBg}`}>
                <Mic className={`w-4 h-4 ${ts}`} />
              </button>
              <button onClick={handleSend} className={`w-8 h-8 rounded-xl flex items-center justify-center transition-colors ${message.trim() ? 'bg-gradient-to-br from-indigo-500 to-purple-600' : (isDark ? 'bg-white/[0.06]' : 'bg-black/[0.04]')}`} data-testid="campaign-send-btn">
                <Send className={`w-3.5 h-3.5 ${message.trim() ? 'text-white' : ts}`} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  /* ── Fullscreen flows (Deep Dive / New Campaign) ── */
  if (deepDiveId) {
    return (
      <V4AdHocChat
        isDark={isDark}
        scenarioId={deepDiveId}
        scenarios={PRIYA_CAMPAIGN_DEEP_DIVES}
        backLabel="Back to Campaigns"
        onBack={handleBackFromDeepDive}
      />
    );
  }

  if (newCampaignFlow) {
    return (
      <V4AdHocChat
        isDark={isDark}
        scenarioId="new-campaign"
        scenarios={PRIYA_CAMPAIGN_CREATE_SCENARIOS}
        backLabel="Back to Campaigns"
        onBack={handleBackFromNewCampaign}
      />
    );
  }

  /* ── Chat view ── */
  if (chatMode) {
    return (
      <div className="flex flex-col h-full" data-testid="campaign-chat-view">
        <div className={`px-4 py-3 flex-shrink-0 border-b ${border}`}>
          <button onClick={() => setChatMode(false)} className={`flex items-center gap-2 px-2 py-1 rounded-lg transition-colors ${hoverBg}`} data-testid="campaign-back-btn">
            <ArrowLeft className={`w-4 h-4 ${ts}`} />
            <span className={`text-sm font-medium ${tp}`}>Back to Campaigns</span>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          <div className="flex justify-center">
            <div className="w-[80%] py-5 space-y-5">
              {chatMessages.map((msg, i) => <ChatMessage key={i} msg={msg} isDark={isDark} />)}
              <div ref={messagesEndRef} />
            </div>
          </div>
        </div>
        {chatInput}
      </div>
    );
  }

  /* ── Data view ── */
  return (
    <div className="flex flex-col h-full" data-testid="campaign-page">
      {/* Header */}
      <div className="px-5 pt-4 pb-3 flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <Target className={`w-5 h-5 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
            <p className={`text-base font-semibold ${tp}`}>Campaigns</p>
            <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${isDark ? 'bg-indigo-400/10 text-indigo-400' : 'bg-indigo-600/10 text-indigo-600'}`}>
              {filtered.length} of {allCampaigns.length}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleCreateCampaign} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-colors bg-gradient-to-br from-indigo-500 to-purple-600 text-white hover:opacity-90`} data-testid="campaign-create-btn">
              <Plus className="w-3.5 h-3.5" /> New Campaign
            </button>
          </div>
        </div>

        {/* Stats row */}
        <SummaryStats campaigns={allCampaigns} isDark={isDark} />

        {/* Search + filter toggle */}
        <div className="flex items-center gap-2 mt-3">
          <div className={`flex items-center flex-1 gap-2 px-3 py-1.5 rounded-lg border ${border} ${isDark ? 'bg-white/[0.02]' : 'bg-black/[0.01]'}`}>
            <Search className={`w-3.5 h-3.5 ${ts}`} />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search campaigns..."
              className={`w-full bg-transparent outline-none text-xs ${tp} placeholder:${ts}`}
              data-testid="campaign-search"
            />
            {search && (
              <button onClick={() => setSearch('')} className={ts}>
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium border ${showFilters ? pillActive : `${border} ${ts}`} transition-colors`}
            data-testid="campaign-filter-toggle"
          >
            <Filter className="w-3.5 h-3.5" /> Filters
          </button>
        </div>

        {/* Filter pills */}
        {showFilters && (
          <div className="flex flex-wrap gap-4 mt-3">
            {/* Status */}
            <div className="flex items-center gap-1.5">
              <span className={`text-[10px] ${ts}`}>Status</span>
              {CAMPAIGN_FILTERS.statuses.map(s => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`px-2 py-0.5 rounded-md text-[10px] font-medium border transition-colors ${statusFilter === s ? pillActive : `${border} ${ts} ${hoverBg}`}`}
                  data-testid={`filter-status-${s}`}
                >
                  {s === 'all' ? 'All' : STATUS_STYLES[s]?.label || s}
                </button>
              ))}
            </div>
            {/* Channel */}
            <div className="flex items-center gap-1.5">
              <span className={`text-[10px] ${ts}`}>Channel</span>
              {CAMPAIGN_FILTERS.channels.map(c => (
                <button
                  key={c}
                  onClick={() => setChannelFilter(c)}
                  className={`px-2 py-0.5 rounded-md text-[10px] font-medium border transition-colors ${channelFilter === c ? pillActive : `${border} ${ts} ${hoverBg}`}`}
                  data-testid={`filter-channel-${c}`}
                >
                  {c === 'all' ? 'All' : CHANNEL_LABELS[c] || c}
                </button>
              ))}
            </div>
            {/* Agent */}
            <div className="flex items-center gap-1.5">
              <span className={`text-[10px] ${ts}`}>Agent</span>
              {CAMPAIGN_FILTERS.agents.map(a => (
                <button
                  key={a}
                  onClick={() => setAgentFilter(a)}
                  className={`px-2 py-0.5 rounded-md text-[10px] font-medium border transition-colors ${agentFilter === a ? pillActive : `${border} ${ts} ${hoverBg}`}`}
                  data-testid={`filter-agent-${a}`}
                >
                  {a === 'all' ? 'All' : AGENT_NAMES[a] || a}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className={`border-t ${border}`} />

      {/* Campaign list */}
      <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4">
        <div className="grid grid-cols-2 gap-4">
          {filtered.map(c => (
            <CampaignCard key={c.id} campaign={c} isDark={isDark} onDeepDive={handleDeepDive} onDetails={setDetailsId} />
          ))}
          {filtered.length === 0 && (
            <div className={`col-span-2 text-center py-12 ${ts}`}>
              <Target className={`w-8 h-8 mx-auto mb-2 ${ts}`} />
              <p className="text-sm">No campaigns match your filters</p>
            </div>
          )}
        </div>
      </div>

      {/* Chat input */}
      {chatInput}

      {/* Details drawer */}
      {detailsId && (
        <DetailsPanel
          campaign={allCampaigns.find((c) => c.id === detailsId)}
          isDark={isDark}
          onClose={() => setDetailsId(null)}
          onApproveAction={handleApproveAction}
        />
      )}
    </div>
  );
};

export default V4MktCampaigns;
